import React, { useState, useEffect } from 'react';
import {
  CForm,
  CRow,
  CCol,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CButton,
  CSpinner,
  CAlert,
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const OrderStatusInputs = ({ 
  onSubmit, 
  onCancel, 
  isLoading = false, 
  submitResponse = null,
  initialData = null,
  isEdit = false 
}) => {
  const [formData, setFormData] = useState({
    orderStatusName: '',
    orderStatusDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      setFormData({
        orderStatusName: initialData.OrderStatusName || '',
        orderStatusDescription: initialData.OrderStatusDescription || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            orderStatusName: '',
            orderStatusDescription: ''
          });
        }
        setShowSuccess(false);
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.orderStatusName.trim()) {
      newErrors.orderStatusName = t('orderStatusNameRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (!userId || userId === 'undefined') {
        setErrors({ general: 'User session not found. Please login again.' });
        return;
      }

      const submitData = {
        SessionUserId: String(userId),
        OrderStatusName: formData.orderStatusName.trim(),
        OrderStatusDescription: formData.orderStatusDescription.trim()
      };

      if (isEdit && initialData?.OrderStatusId) {
        submitData.OrderStatusId = initialData.OrderStatusId;
      }

      console.log(`Submitting ${isEdit ? 'update' : 'create'} order data:`, submitData);
      onSubmit(submitData);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData({
        orderStatusName: '',
        orderStatusDescription: ''
      });
    }
    setErrors({});
    onCancel();
  };

  const getSuccessMessage = () => {
    return isEdit ? t('orderStatusUpdated') : t('orderStatusCreated');
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit ? t('updating') : t('creating');
    }
    return isEdit ? t('updateOrderStatus') : t('createOrderStatus');
  };

  return (
    <div>
      {showSuccess && (
        <CAlert color="success" dismissible onClose={() => setShowSuccess(false)}>
          {getSuccessMessage()}
        </CAlert>
      )}

      {errors.general && (
        <CAlert color="danger" 
        dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      {submitResponse?.Result?.ErrorCode !== 0 && submitResponse?.Result?.ErrorDescription && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit} noValidate>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="orderStatusName">
              {t('orderStatusName')}
            </CFormLabel>
            <CFormInput
              type="text"
              id="orderStatusName"
              name="orderStatusName"
              value={formData.orderStatusName}
              onChange={handleInputChange}
              invalid={!!errors.orderStatusName}
              placeholder={t('enterOrderStatusName')}
              disabled={isLoading}
            />
            {errors.orderStatusName && (
              <div className="invalid-feedback d-block">
                {errors.orderStatusName}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-4">
          <CCol md={12}>
            <CFormLabel htmlFor="orderStatusDescription">
              {t('orderStatusDescription')}
            </CFormLabel>
            <CFormTextarea
              id="orderStatusDescription"
              name="orderStatusDescription"
              rows={4}
              value={formData.orderStatusDescription}
              onChange={handleInputChange}
              invalid={!!errors.orderStatusDescription}
              placeholder={t('enterOrderStatusDescription')}
              disabled={isLoading}
            />
            {errors.orderStatusDescription && (
              <div className="invalid-feedback d-block">
                {errors.orderStatusDescription}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>

            <CButton
              color="secondary"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              <CIcon icon={cilX} className="me-2" />
              {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>

      {/* Debug info - development only */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-3 bg-light rounded">
          <small>
            <strong>Debug Info:</strong><br/>
            Is Edit Mode: {String(isEdit)}<br/>
            Form Data: {JSON.stringify(formData, null, 2)}<br/>
            Initial Data: {JSON.stringify(initialData, null, 2)}<br/>
            Errors: {JSON.stringify(errors, null, 2)}<br/>
            Is Loading: {String(Boolean(isLoading))}<br/>
            Submit Response: {submitResponse ? JSON.stringify(submitResponse, null, 2) : 'None'}
          </small>
        </div>
      )} */}
    </div>
  );
};

OrderStatusInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default OrderStatusInputs;